// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Durable outbound queue for score.update / hole.event / shot.mark WebSocket
// messages. Entries persist across app restarts (AsyncStorage on native,
// localStorage on web) and flush when the hole screen's socket reconnects.
// Safe to replay: score.update is an idempotent last-write-wins upsert
// server-side, and hole.event / shot.mark carry a client-generated id the
// backend dedupes on.

import type { HoleEntry, HoleEventEntry } from '../features/hole/types';

type AsyncStorageLike = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
};

export type OfflineQueueKind = 'score.update' | 'hole.event' | 'shot.mark';

export type QueueEntry = {
  eventId: string;
  kind: OfflineQueueKind;
  roundId: string;
  payload: Record<string, unknown>;
  meta?: { playerName?: string };
  enqueuedAt: string;
  status: 'pending' | 'inflight';
  attempts: number;
  // Transient: when the current inflight send happened. Not restored on app
  // restart (inflight entries revert to pending on load).
  sentAt?: string;
};

export type OfflineQueueState = {
  pendingCount: number;
  syncing: boolean;
  lastSyncedAt: string | null;
  deadLetteredCount: number;
  lastRejection: { kind: OfflineQueueKind; code: string; message?: string } | null;
};

export type OfflineQueueContext = {
  subjectKey: string;
  roundId: string;
};

type QueueClient = {
  send(payload: Record<string, unknown>): 'sent' | 'queued' | 'dropped';
  isConnected(): boolean;
  onConnectionChange(handler: (status: string) => void): () => void;
  onMessage(handler: (message: unknown) => void): () => void;
};

// The send-only subset, for callers that manage their own socket lifecycle.
export type QueueSender = Pick<QueueClient, 'send' | 'isConnected'>;

type Envelope = {
  version: 1;
  entries: QueueEntry[];
  deadLetteredCount: number;
};

const STORAGE_VERSION = 1;
const MAX_ENTRIES_PER_SUBJECT = 500;
const MAX_FLUSH_ATTEMPTS = 25;
const MAX_ENTRY_AGE_MS = 14 * 24 * 60 * 60 * 1000;
// How long an inflight send may wait for its ack before it reverts to pending
// (a send into a half-dead socket succeeds locally but never arrives).
const ACK_TIMEOUT_MS = 10000;
const ACK_SWEEP_INTERVAL_MS = 5000;

const storageKeyFor = (subjectKey: string) => `goffy:offline-queue:v${STORAGE_VERSION}:${subjectKey}`;

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

let storageOverride: AsyncStorageLike | null = null;

const readRaw = async (key: string): Promise<string | null> => {
  if (storageOverride) return storageOverride.getItem(key);
  const local = getLocalStorage();
  if (local) return local.getItem(key);
  if (asyncStorage) return asyncStorage.getItem(key);
  return null;
};

const writeRaw = async (key: string, value: string): Promise<void> => {
  if (storageOverride) return storageOverride.setItem(key, value);
  const local = getLocalStorage();
  if (local) {
    local.setItem(key, value);
    return;
  }
  if (asyncStorage) await asyncStorage.setItem(key, value);
};

const removeRaw = async (key: string): Promise<void> => {
  if (storageOverride) return storageOverride.removeItem(key);
  const local = getLocalStorage();
  if (local) {
    local.removeItem(key);
    return;
  }
  if (asyncStorage) await asyncStorage.removeItem(key);
};

export const createEventId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC4122-ish fallback for older native runtimes.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const normalizeEntry = (value: unknown): QueueEntry | null => {
  if (!value || typeof value !== 'object') return null;
  const input = value as Record<string, unknown>;
  if (typeof input.eventId !== 'string' || !input.eventId) return null;
  if (input.kind !== 'score.update' && input.kind !== 'hole.event' && input.kind !== 'shot.mark') return null;
  if (typeof input.roundId !== 'string' || !input.roundId) return null;
  if (!input.payload || typeof input.payload !== 'object') return null;
  return {
    eventId: input.eventId,
    kind: input.kind,
    roundId: input.roundId,
    payload: input.payload as Record<string, unknown>,
    meta: input.meta && typeof input.meta === 'object' ? (input.meta as QueueEntry['meta']) : undefined,
    enqueuedAt: typeof input.enqueuedAt === 'string' ? input.enqueuedAt : new Date(0).toISOString(),
    status: input.status === 'inflight' ? 'inflight' : 'pending',
    attempts: typeof input.attempts === 'number' && Number.isFinite(input.attempts) ? input.attempts : 0,
  };
};

const normalizeEnvelope = (value: unknown): Envelope | null => {
  if (!value || typeof value !== 'object') return null;
  const input = value as Record<string, unknown>;
  if (input.version !== STORAGE_VERSION || !Array.isArray(input.entries)) return null;
  const entries = input.entries
    .map(normalizeEntry)
    .filter((entry): entry is QueueEntry => entry !== null)
    // A restart while entries were inflight means the sends may or may not
    // have arrived; treat them as pending again (resends are deduped).
    .map((entry) => (entry.status === 'inflight' ? { ...entry, status: 'pending' as const } : entry));
  return {
    version: STORAGE_VERSION,
    entries,
    deadLetteredCount:
      typeof input.deadLetteredCount === 'number' && Number.isFinite(input.deadLetteredCount)
        ? input.deadLetteredCount
        : 0,
  };
};

type SubjectState = {
  envelope: Envelope;
  loaded: boolean;
  loadPromise: Promise<void> | null;
  syncing: boolean;
  lastSyncedAt: string | null;
  lastRejection: OfflineQueueState['lastRejection'];
};

const subjects = new Map<string, SubjectState>();
const listeners = new Set<(state: OfflineQueueState, subjectKey: string) => void>();

const getSubject = (subjectKey: string): SubjectState => {
  let state = subjects.get(subjectKey);
  if (!state) {
    state = {
      envelope: { version: STORAGE_VERSION, entries: [], deadLetteredCount: 0 },
      loaded: false,
      loadPromise: null,
      syncing: false,
      lastSyncedAt: null,
      lastRejection: null,
    };
    subjects.set(subjectKey, state);
  }
  return state;
};

const applyGiveUpPolicy = (state: SubjectState) => {
  const cutoff = Date.now() - MAX_ENTRY_AGE_MS;
  const kept: QueueEntry[] = [];
  for (const entry of state.envelope.entries) {
    const enqueuedAtMs = Date.parse(entry.enqueuedAt);
    const tooOld = Number.isFinite(enqueuedAtMs) && enqueuedAtMs < cutoff;
    if (tooOld || entry.attempts >= MAX_FLUSH_ATTEMPTS) {
      state.envelope.deadLetteredCount += 1;
    } else {
      kept.push(entry);
    }
  }
  state.envelope.entries = kept;
};

const ensureLoaded = async (subjectKey: string): Promise<SubjectState> => {
  const state = getSubject(subjectKey);
  if (state.loaded) return state;
  if (!state.loadPromise) {
    state.loadPromise = (async () => {
      try {
        const raw = await readRaw(storageKeyFor(subjectKey));
        if (raw) {
          const parsed = normalizeEnvelope(JSON.parse(raw));
          if (parsed) {
            state.envelope = parsed;
          }
        }
      } catch {
        // Corrupt payload: start fresh rather than blocking writes.
      }
      applyGiveUpPolicy(state);
      state.loaded = true;
    })();
  }
  await state.loadPromise;
  state.loadPromise = null;
  return state;
};

const persist = async (subjectKey: string, state: SubjectState): Promise<void> => {
  const key = storageKeyFor(subjectKey);
  try {
    if (state.envelope.entries.length === 0 && state.envelope.deadLetteredCount === 0) {
      await removeRaw(key);
      return;
    }
    await writeRaw(key, JSON.stringify(state.envelope));
  } catch {
    // Storage full/unavailable: the in-memory queue still works for this session.
  }
};

const stateFor = (state: SubjectState): OfflineQueueState => ({
  pendingCount: state.envelope.entries.length,
  syncing: state.syncing,
  lastSyncedAt: state.lastSyncedAt,
  deadLetteredCount: state.envelope.deadLetteredCount,
  lastRejection: state.lastRejection,
});

const notify = (subjectKey: string) => {
  const state = getSubject(subjectKey);
  const snapshot = stateFor(state);
  listeners.forEach((listener) => listener(snapshot, subjectKey));
};

export const subscribeOfflineQueue = (
  listener: (state: OfflineQueueState, subjectKey: string) => void
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getOfflineQueueState = (subjectKey: string): OfflineQueueState => stateFor(getSubject(subjectKey));

export const getPendingEntries = (subjectKey: string, roundId?: string): QueueEntry[] => {
  const state = getSubject(subjectKey);
  return state.envelope.entries.filter((entry) => !roundId || entry.roundId === roundId);
};

export const loadOfflineQueue = async (subjectKey: string): Promise<QueueEntry[]> => {
  const state = await ensureLoaded(subjectKey);
  return state.envelope.entries.slice();
};

export const enqueueOfflineWrite = async (
  ctx: OfflineQueueContext,
  kind: OfflineQueueKind,
  eventId: string,
  payload: Record<string, unknown>,
  meta?: QueueEntry['meta']
): Promise<void> => {
  const state = await ensureLoaded(ctx.subjectKey);
  const entry: QueueEntry = {
    eventId,
    kind,
    roundId: ctx.roundId,
    payload,
    meta,
    enqueuedAt: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
  };

  if (kind === 'score.update') {
    // The backend upsert is last-write-wins per (player, hole): only the
    // latest score for a hole matters, so coalesce. Inflight entries are
    // superseded too — a re-tap replaces the awaiting-ack copy.
    const playerId = payload.playerId;
    const hole = payload.hole;
    state.envelope.entries = state.envelope.entries.filter(
      (existing) =>
        !(
          existing.kind === 'score.update' &&
          existing.roundId === ctx.roundId &&
          existing.payload.playerId === playerId &&
          existing.payload.hole === hole
        )
    );
  }

  state.envelope.entries.push(entry);

  if (state.envelope.entries.length > MAX_ENTRIES_PER_SUBJECT) {
    // Never drop scores: coalescing already bounds them at players × holes.
    // Shot marks go first — they are sensor telemetry, hazards are user input.
    const dropIndex = ['shot.mark', 'hole.event']
      .map((droppableKind) =>
        state.envelope.entries.findIndex(
          (candidate) => candidate.kind === droppableKind && candidate.status === 'pending'
        )
      )
      .find((index) => index >= 0);
    if (dropIndex !== undefined) {
      state.envelope.entries.splice(dropIndex, 1);
      state.envelope.deadLetteredCount += 1;
    }
  }

  await persist(ctx.subjectKey, state);
  notify(ctx.subjectKey);
};

/**
 * Send a score.update / hole.event message now if the socket is connected,
 * otherwise persist it durably for a later flush. 'queued' is a success
 * outcome: the entry survives app restarts and is replay-safe server-side.
 */
export const sendOrQueue = async (
  client: QueueSender | null,
  ctx: OfflineQueueContext,
  kind: OfflineQueueKind,
  eventId: string,
  payload: Record<string, unknown>,
  meta?: QueueEntry['meta']
): Promise<'sent' | 'queued'> => {
  if (client && client.isConnected()) {
    // Only a real write counts: a frame the ws client itself queued must still land in THIS
    // durable queue, or it would not survive an app restart.
    if (client.send(payload) === 'sent') {
      return 'sent';
    }
  }
  await enqueueOfflineWrite(ctx, kind, eventId, payload, meta);
  return 'queued';
};

/**
 * Durable send: the entry is persisted BEFORE the send and only removed by a
 * positive ack (a hole.update echoing the requestId, or semantically matching
 * the queued value) or an explicit rejection. This closes the
 * connected-but-dead-socket window where socket.send succeeds locally but the
 * frame never reaches the server. Unacked sends revert to pending after
 * ACK_TIMEOUT_MS and are re-flushed (replays are idempotent server-side).
 */
export const sendWithAck = async (
  client: QueueSender | null,
  ctx: OfflineQueueContext,
  kind: OfflineQueueKind,
  eventId: string,
  payload: Record<string, unknown>,
  meta?: QueueEntry['meta']
): Promise<'sent-pending-ack' | 'queued'> => {
  await enqueueOfflineWrite(ctx, kind, eventId, payload, meta);
  if (!client || !client.isConnected()) {
    return 'queued';
  }
  const state = getSubject(ctx.subjectKey);
  const entry = state.envelope.entries.find((candidate) => candidate.eventId === eventId);
  if (!entry) {
    // Entry was coalesced away or dropped; nothing left to track.
    return 'queued';
  }
  if (client.send(payload) !== 'sent') {
    return 'queued';
  }
  entry.status = 'inflight';
  entry.sentAt = new Date().toISOString();
  state.syncing = true;
  await persist(ctx.subjectKey, state);
  notify(ctx.subjectKey);
  return 'sent-pending-ack';
};

const rejectionMatches = (entry: QueueEntry, message: Record<string, unknown>): boolean => {
  if (entry.kind === 'hole.event') {
    return typeof message.eventId === 'string' && message.eventId === entry.eventId;
  }
  if (entry.kind === 'shot.mark') {
    return typeof message.markId === 'string' && message.markId === entry.eventId;
  }
  return typeof message.requestId === 'string' && message.requestId === entry.eventId;
};

const flush = async (subjectKey: string, client: QueueSender): Promise<void> => {
  const state = await ensureLoaded(subjectKey);
  const toSend = state.envelope.entries.filter((entry) => entry.status === 'pending');
  if (toSend.length === 0) return;

  state.syncing = true;
  const now = new Date().toISOString();
  toSend.forEach((entry) => {
    entry.status = 'inflight';
    entry.sentAt = now;
  });
  await persist(subjectKey, state);
  notify(subjectKey);

  for (const entry of toSend) {
    if (!client.isConnected()) break;
    client.send(entry.payload);
  }
};

// Clears `syncing` once nothing is awaiting an ack anymore.
const settleSyncState = (state: SubjectState, acked: boolean) => {
  if (acked) {
    state.lastSyncedAt = new Date().toISOString();
  }
  if (state.syncing && !state.envelope.entries.some((entry) => entry.status === 'inflight')) {
    state.syncing = false;
  }
};

const revertInflight = async (subjectKey: string): Promise<void> => {
  const state = getSubject(subjectKey);
  let changed = false;
  state.envelope.entries.forEach((entry) => {
    if (entry.status === 'inflight') {
      entry.status = 'pending';
      entry.attempts += 1;
      delete entry.sentAt;
      changed = true;
    }
  });
  if (!changed) {
    if (state.syncing) {
      state.syncing = false;
      notify(subjectKey);
    }
    return;
  }
  applyGiveUpPolicy(state);
  state.syncing = false;
  await persist(subjectKey, state);
  notify(subjectKey);
};

/**
 * Positive-ack matching for a queued score. A hole.update frame confirms a
 * queued score when it echoes the score's requestId (new backends), or — the
 * fallback for backends that don't echo it — when the frame's authoritative
 * entries for the same round+hole contain exactly the queued value.
 */
const matchesScoreAck = (entry: QueueEntry, frame: Record<string, unknown>): boolean => {
  if (entry.kind !== 'score.update') return false;
  if (typeof frame.requestId === 'string' && frame.requestId.length > 0) {
    if (frame.requestId === entry.eventId) return true;
    // A requestId belonging to a different score says nothing about this entry;
    // fall through to the semantic check.
  }
  if (frame.roundId !== entry.roundId) return false;
  const hole = typeof frame.hole === 'number' ? frame.hole : null;
  if (hole === null || hole !== entry.payload.hole) return false;
  const pickup = entry.payload.pickup === true;
  const expectedGross = pickup
    ? null
    : typeof entry.payload.strokes === 'number'
      ? entry.payload.strokes
      : null;
  const frameEntries = Array.isArray(frame.entries) ? frame.entries : [];
  return frameEntries.some((candidate) => {
    if (!candidate || typeof candidate !== 'object') return false;
    const record = candidate as Record<string, unknown>;
    const candidateGross = typeof record.grossStrokes === 'number' ? record.grossStrokes : null;
    return (
      record.playerId === entry.payload.playerId &&
      candidateGross === expectedGross &&
      (record.pickup === true) === pickup
    );
  });
};

const acknowledgeScoreEntries = async (subjectKey: string, frame: Record<string, unknown>): Promise<void> => {
  const state = await ensureLoaded(subjectKey);
  const before = state.envelope.entries.length;
  state.envelope.entries = state.envelope.entries.filter((entry) => !matchesScoreAck(entry, frame));
  if (state.envelope.entries.length === before) return;
  settleSyncState(state, true);
  await persist(subjectKey, state);
  notify(subjectKey);
};

// Legacy confirmation for hole.event and shot.mark entries: the backend has no
// per-event positive ack for either yet, so any inbound non-error frame still
// confirms them.
const acknowledgeInflightHoleEvents = async (subjectKey: string): Promise<void> => {
  const state = getSubject(subjectKey);
  const remaining = state.envelope.entries.filter(
    (entry) => !((entry.kind === 'hole.event' || entry.kind === 'shot.mark') && entry.status === 'inflight')
  );
  if (remaining.length === state.envelope.entries.length) return;
  state.envelope.entries = remaining;
  settleSyncState(state, true);
  await persist(subjectKey, state);
  notify(subjectKey);
};

/**
 * Reverts inflight entries whose ack never arrived back to pending (bumping
 * attempts, bounded by the give-up policy) and re-flushes when connected.
 * Called on an interval while the queue is attached; exported for tests.
 */
export const sweepUnackedEntries = async (subjectKey: string, client: QueueSender | null): Promise<void> => {
  const state = getSubject(subjectKey);
  const now = Date.now();
  let reverted = false;
  state.envelope.entries.forEach((entry) => {
    if (entry.status !== 'inflight') return;
    const sentAtMs = entry.sentAt ? Date.parse(entry.sentAt) : Number.NaN;
    if (!Number.isFinite(sentAtMs) || now - sentAtMs >= ACK_TIMEOUT_MS) {
      entry.status = 'pending';
      entry.attempts += 1;
      delete entry.sentAt;
      reverted = true;
    }
  });
  if (reverted) {
    applyGiveUpPolicy(state);
    settleSyncState(state, false);
    await persist(subjectKey, state);
    notify(subjectKey);
    if (client && client.isConnected()) {
      await flush(subjectKey, client);
    }
    return;
  }
  if (state.syncing && !state.envelope.entries.some((entry) => entry.status === 'inflight')) {
    state.syncing = false;
    notify(subjectKey);
  }
};

const handleRejection = async (
  subjectKey: string,
  kind: OfflineQueueKind,
  message: Record<string, unknown>
): Promise<void> => {
  const state = await ensureLoaded(subjectKey);
  const before = state.envelope.entries.length;
  state.envelope.entries = state.envelope.entries.filter(
    (entry) => !(entry.kind === kind && rejectionMatches(entry, message))
  );
  state.lastRejection = {
    kind,
    code: typeof message.code === 'string' ? message.code : 'rejected',
    message: typeof message.message === 'string' ? message.message : undefined,
  };
  if (state.envelope.entries.length !== before) {
    await persist(subjectKey, state);
  }
  notify(subjectKey);
};

/**
 * Attach the queue to the hole screen's socket: flushes pending entries when
 * the connection comes up, removes score entries only on a positive
 * hole.update ack (requestId echo, or semantic match against the frame's
 * entries), reverts in-flight entries when the connection drops, and re-sends
 * entries whose ack never arrived.
 */
export const attachOfflineQueue = (client: QueueClient, ctx: OfflineQueueContext): (() => void) => {
  const disposeConnection = client.onConnectionChange((status) => {
    if (status === 'connected') {
      void flush(ctx.subjectKey, client);
    } else if (status === 'disconnected') {
      void revertInflight(ctx.subjectKey);
    }
  });

  const disposeMessages = client.onMessage((message) => {
    if (!message || typeof message !== 'object') return;
    const frame = message as Record<string, unknown>;
    if (frame.type === 'score.update.rejected') {
      void handleRejection(ctx.subjectKey, 'score.update', frame);
      return;
    }
    if (frame.type === 'hole.event.rejected') {
      void handleRejection(ctx.subjectKey, 'hole.event', frame);
      return;
    }
    if (frame.type === 'error') {
      // Failure signals (e.g. ws.handler.failed) must never count as delivery.
      return;
    }
    if (frame.type === 'hole.update') {
      void acknowledgeScoreEntries(ctx.subjectKey, frame);
    }
    void acknowledgeInflightHoleEvents(ctx.subjectKey);
  });

  const sweepTimer = setInterval(() => {
    void sweepUnackedEntries(ctx.subjectKey, client);
  }, ACK_SWEEP_INTERVAL_MS);

  void ensureLoaded(ctx.subjectKey).then(() => {
    notify(ctx.subjectKey);
    if (client.isConnected()) {
      void flush(ctx.subjectKey, client);
    }
  });

  return () => {
    clearInterval(sweepTimer);
    disposeConnection();
    disposeMessages();
  };
};

export const acknowledgeRejection = (subjectKey: string): void => {
  const state = getSubject(subjectKey);
  if (!state.lastRejection && state.envelope.deadLetteredCount === 0) return;
  state.lastRejection = null;
  state.envelope.deadLetteredCount = 0;
  void persist(subjectKey, state);
  notify(subjectKey);
};

/**
 * Overlay pending offline writes on a cached snapshot so a re-opened screen
 * shows offline-entered scores, hazards and mystery winners. Pure function.
 */
export const applyPendingToSnapshot = (
  entries: QueueEntry[],
  snapshot: { holeEntries: HoleEntry[]; holeEvents: HoleEventEntry[] }
): { holeEntries: HoleEntry[]; holeEvents: HoleEventEntry[] } => {
  let holeEntries = snapshot.holeEntries.slice();
  const holeEvents = snapshot.holeEvents.slice();

  for (const entry of entries) {
    if (entry.kind === 'score.update') {
      const playerId = typeof entry.payload.playerId === 'string' ? entry.payload.playerId : null;
      const hole = typeof entry.payload.hole === 'number' ? entry.payload.hole : null;
      if (!playerId || hole === null) continue;
      const strokes = typeof entry.payload.strokes === 'number' ? entry.payload.strokes : null;
      const pickup = entry.payload.pickup === true;
      const existingIndex = holeEntries.findIndex(
        (candidate) => candidate.playerId === playerId && candidate.holeNumber === hole
      );
      if (existingIndex >= 0) {
        holeEntries = holeEntries.slice();
        holeEntries[existingIndex] = {
          ...holeEntries[existingIndex],
          grossStrokes: pickup ? null : strokes,
          pickup,
        };
      } else {
        holeEntries.push({
          id: entry.eventId,
          roundId: entry.roundId,
          holeNumber: hole,
          playerId,
          playerName: entry.meta?.playerName ?? '',
          grossStrokes: pickup ? null : strokes,
          pickup,
          createdAt: entry.enqueuedAt,
        });
      }
      continue;
    }

    const eventDelta = typeof entry.payload.eventDelta === 'number' ? entry.payload.eventDelta : 1;
    const playerId = typeof entry.payload.playerId === 'string' ? entry.payload.playerId : null;
    const holeNumber = typeof entry.payload.holeNumber === 'number' ? entry.payload.holeNumber : null;
    const eventType = typeof entry.payload.eventType === 'string' ? entry.payload.eventType : null;
    if (!playerId || holeNumber === null || !eventType) continue;
    const eventValue = typeof entry.payload.eventValue === 'string' ? entry.payload.eventValue : null;
    const occurredAt = typeof entry.payload.timestamp === 'string' ? entry.payload.timestamp : entry.enqueuedAt;

    const matchIndex = (candidates: HoleEventEntry[]) =>
      candidates.findIndex(
        (candidate) =>
          candidate.playerId === playerId &&
          candidate.holeNumber === holeNumber &&
          candidate.eventType === eventType &&
          (candidate.eventValue ?? null) === eventValue &&
          (candidate.occurredAt ?? null) === occurredAt
      );

    if (eventDelta < 0) {
      // Undo: drop the most recent matching event (value + type + hole).
      for (let i = holeEvents.length - 1; i >= 0; i--) {
        const candidate = holeEvents[i];
        if (
          candidate.playerId === playerId &&
          candidate.holeNumber === holeNumber &&
          candidate.eventType === eventType &&
          (eventValue === null || (candidate.eventValue ?? null) === eventValue)
        ) {
          holeEvents.splice(i, 1);
          break;
        }
      }
    } else if (matchIndex(holeEvents) < 0) {
      holeEvents.push({ playerId, holeNumber, eventType, eventValue, occurredAt });
    }
  }

  return { holeEntries, holeEvents };
};

// Test hooks — not for production use.
export const __setOfflineQueueStorageForTests = (storage: AsyncStorageLike | null) => {
  storageOverride = storage;
};

export const __resetOfflineQueueForTests = () => {
  subjects.clear();
  listeners.clear();
};
